import {ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core'
import {CartItemComponent, CartItemContextSource} from '@spartacus/cart/base/components'
import {CartItemComponentOptions, CartItemContext} from '@spartacus/cart/base/root'
import {Price} from '@spartacus/core'
import {BehaviorSubject, Observable, Subscription} from 'rxjs'
import {take} from 'rxjs/operators'
import {EbAnalyticsService} from "../../../analytics/services/facades/eb-analytics.service"
import {EbAvailability, EbAvailabilityItem} from '../../../product/interfaces/eb-availability.interface'
import {EbProduct, EbProductPrice} from '../../../product/interfaces/eb-product.interface'
import {EbAvailabilityService} from '../../../product/services/facades/eb-availability.service'
import {EbOrderEntry} from '../../interface/eb-order-entry.interface'
import {EbActiveCartService} from "../../services/eb-active-cart.service";

@Component({
    selector: 'eb-cart-item',
    templateUrl: './eb-cart-item.component.html',
    styleUrls: ['./eb-cart-item.component.scss'],
    providers: [
        CartItemContextSource,
        { provide: CartItemContext, useExisting: CartItemContextSource }
    ],
    standalone: false
})
export class EbCartItemComponent extends CartItemComponent implements OnInit, OnDestroy, OnChanges {
  @Input() compact = false
  @Input() item: EbOrderEntry
  @Input() readonly = false
  @Input() showPrice = true
  @Input() isAvailabilityProvidedByParent = false
  @Input() showAddToCart: boolean
  @Input() showAvailability = true
  @Input() hideQuantity = false
  @Input() options: CartItemComponentOptions = {
    isSaveForLater: false,
    optionalBtn: null
  }
  @Input() cartPopUp = false
  priceLoading = true
  availabilityLoading = true
  availabilityItem: EbAvailabilityItem
  priceItem$: BehaviorSubject<Price> =  new BehaviorSubject(null);
  availabilityError: boolean
  subscriptions = new Subscription()
  minOrderQuantity: number
  cartEntries$: Observable<EbOrderEntry[]>;

  @Input('availability')
  set availability(availability: EbAvailabilityItem) {
    if (!availability || Object.keys(availability).length === 0) {
      this.availabilityLoading = true
    } else if (Object.keys(availability).length > 0) {
      this.availabilityLoading = false
      this.availabilityItem = availability
    }
    this.cd.detectChanges()
  }

  @Input('prices')
  set prices(prices: EbProductPrice) {
    if (!prices || Object.keys(prices).length === 0) {
      this.priceLoading = true
    } else if (Object.keys(prices).length > 0) {
      this.priceLoading = false
      this.updatePrice(prices)
    }
    this.cd.detectChanges()
  }

  constructor(
    cartItemContextSource: CartItemContextSource,
    protected availabilityService: EbAvailabilityService,
    protected analyticsService: EbAnalyticsService,
    protected cd: ChangeDetectorRef,
    protected activeCartService: EbActiveCartService,
  ) {
    super(cartItemContextSource)
  }
  ngOnInit() {
    if (this.shouldUpdateAvailability()) {
      this.updateAvailability()
    } else if (!this.item?.product.orderable) {
      this.priceLoading = false
    }
    this.updatePrice()
    this.setMinOrderQuantity()
    this.cartEntries$ = this.activeCartService.getEntries()
  }

  ngOnChanges(changes?: SimpleChanges) {
    super.ngOnChanges(changes);
    this.setMinOrderQuantity();
  }

  updatePrice(providedPrice?: EbProductPrice) {
    const hasBasePrice = this.item?.basePrice && this.item?.basePrice.value > 0
    this.priceItem$.next(hasBasePrice
      ? this.item?.basePrice
      : providedPrice?.items?.[0]?.itemNetPricePerUnit)
  }

  onEnter(counterRef: any) {
    counterRef.input.nativeElement.blur()
  }

  updateAvailability() {
    this.availabilityLoading = true
    const fetchingObservable = this.availabilityService.fetchAvailability({
      product: this.item?.product?.code,
      quantity: this.item?.quantity
    })
    const subscriber = (availabilityItem: EbAvailability) => {
      if (availabilityItem.items) {
        const availabilityItemResult = availabilityItem.items.find(
          item => item?.product === this.item?.product?.code
        )

        if (availabilityItemResult) {
          this.availabilityItem = availabilityItemResult
        }
      }
      this.availabilityError = !!availabilityItem.error
      this.availabilityLoading = false
      this.cd.detectChanges()
    }
    fetchingObservable.pipe(take(1)).subscribe(subscriber)
  }

  getSuccessorLink(productCode: string): string {
    return `/v/${productCode}`
  }

  hasSuccessor(product: EbProduct): boolean {
    return product.successor !== undefined
  }

  shouldUpdateAvailability(): boolean {
    return (
      this.item?.product.orderable && !this.isAvailabilityProvidedByParent && this.showAvailability
    )
  }

  setMinOrderQuantity(): void {
    const minOrderQuantity = this.item?.product?.productSalesView?.minOrderQuantity
    if (minOrderQuantity) {
      this.minOrderQuantity = this.item?.product?.productSalesView?.minOrderQuantity
    } else {
      this.minOrderQuantity = 1
    }
    this.quantityControl?.setValue(Math.ceil(this.item.quantity / this.minOrderQuantity), {
      emitEvent: false
    })
  }

   removeItem(): void {
    this.updateAnalyticsOnRemove()
    super.removeItem()
  }

  updateAnalyticsOnRemove() {
    this.subscriptions.add(this.cartEntries$.pipe(take(1)).subscribe(
      (entries) => {
        this.analyticsService.removeEcommerceItem(entries[0]?.basePrice?.value?.toString(), entries)
      }
    ))
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe()
    this.priceItem$.unsubscribe()
  }
}
